
import React, { useState } from "react";
import { AlertTriangle, Info, Key, Wifi, Database, ServerOff } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ErrorMessageProps {
  errorType?: string;
  errorDetails?: string;
  statusCode?: number;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ errorType, errorDetails, statusCode }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Determine the type of error to show the appropriate message
  const isQuotaError = errorType === "quota_exceeded";
  const isApiKeyError = errorType === "invalid_api_key" || errorType === "api_key_missing";
  const isConnectionError = errorType === "connection_error" || errorType === "supabase_connection_error";
  const isDatabaseError = errorType === "database_error" || errorType === "not_found";
  const isAssistantNotFound = errorType === "assistant_not_found";
  const isEdgeFunctionError = errorType === "edge_function_error" || errorType === "api_error";
  
  // Map to proper variant type for the Alert component (only "default" or "destructive" are allowed)
  const variant = isQuotaError || isAssistantNotFound ? "default" : "destructive";
  
  return (
    <Alert variant={variant} className={`mb-3 ${isQuotaError || isAssistantNotFound ? "border-yellow-500" : ""}`}>
      {isQuotaError && <Info className="h-4 w-4 text-yellow-500" />}
      {isApiKeyError && <Key className="h-4 w-4" />}
      {isConnectionError && <Wifi className="h-4 w-4" />}
      {isDatabaseError && <Database className="h-4 w-4" />}
      {isAssistantNotFound && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
      {isEdgeFunctionError && <ServerOff className="h-4 w-4" />}
      {!isQuotaError && !isApiKeyError && !isConnectionError && !isDatabaseError && !isAssistantNotFound && !isEdgeFunctionError && <AlertTriangle className="h-4 w-4" />}
      
      <div className="flex flex-col space-y-1 w-full">
        <AlertTitle className="text-sm font-medium">
          {isApiKeyError && "API Key Error"}
          {isConnectionError && "Connection Error"}
          {isDatabaseError && "Database Error"}
          {isQuotaError && "Usage Limit Reached"}
          {isAssistantNotFound && "Assistant Not Found"}
          {isEdgeFunctionError && "Edge Function Error"}
          {!isQuotaError && !isApiKeyError && !isConnectionError && !isDatabaseError && !isAssistantNotFound && !isEdgeFunctionError && "Error"}
          {statusCode && ` (Status: ${statusCode})`}
        </AlertTitle>
        
        <AlertDescription className="text-sm">
          {isQuotaError && 
            "Our AI service is temporarily unavailable due to usage limits. We'll be back soon!"}
          {isApiKeyError && 
            "The AI service is not properly configured. Please check your OpenAI API key configuration."}
          {isConnectionError && 
            "Unable to connect to our backend services. Please check your internet connection or try again later."}
          {isDatabaseError && 
            "We're having trouble accessing our database. The system is using default data until the connection is restored."}
          {isAssistantNotFound &&
            "The requested assistant doesn't exist. Using default assistant data instead."}
          {isEdgeFunctionError && 
            "There was an error with the OpenAI service. This is often caused by an invalid API key or exceeded quota limits. Check the Edge Function secrets in the Supabase dashboard."}
          {!isQuotaError && !isApiKeyError && !isConnectionError && !isDatabaseError && !isAssistantNotFound && !isEdgeFunctionError && 
            "Something went wrong with our AI service. Please try again later."}
        </AlertDescription>
        
        {errorDetails && (
          <Collapsible 
            open={showDetails}
            onOpenChange={setShowDetails}
            className="w-full pt-2"
          >
            <CollapsibleTrigger className="flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors">
              {showDetails ? (
                <ChevronUp className="h-3 w-3 mr-1" />
              ) : (
                <ChevronDown className="h-3 w-3 mr-1" />
              )}
              <span>Technical details</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="text-xs bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono overflow-auto max-h-32">
                {statusCode && <div className="font-bold mb-1">Status Code: {statusCode}</div>}
                {errorDetails}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </Alert>
  );
};

export default ErrorMessage;
